
import { Clock, AlertTriangle, Crown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useTrialContext } from '@/contexts/TrialContext';
import { useTrialCountdown } from '@/hooks/use-trial-countdown';
import { useSubscriptionStatus } from '@/hooks/use-subscription-status';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useState } from 'react';

export const TrialStatusWidget = () => {
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const { 
    trial,
    hasActiveTrial, 
    hasExpiredTrial
  } = useTrialContext();
  const { hasActiveSubscription, subscriptionType } = useSubscriptionStatus();
  
  // Use the countdown hook only when we have an active trial
  const { formattedTime, timeRemaining } = useTrialCountdown({
    expiresAt: trial?.expires_at || '',
    isActive: hasActiveTrial
  });

  const handleUpgradeToMonthly = async () => {
    try {
      setIsProcessing(true);
      
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { priceId: 'price_1QhOtZCFL931KD1uLd6J00IU' }
      });

      if (error) throw error;
      if (!data?.url) throw new Error('No checkout URL received');

      window.location.href = data.url;
    } catch (error) {
      console.error('Error in monthly upgrade flow:', error);
      toast({
        title: "Error",
        description: "There was a problem processing your request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Don't show widget if user has active subscription
  if (hasActiveSubscription) {
    if (subscriptionType === 'lifetime') {
      return (
        <Card className="border-purple-200 bg-purple-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Crown className="h-4 w-4 text-purple-600" />
                <div>
                  <Badge className="mb-1 bg-purple-600">
                    Lifetime Member
                  </Badge>
                  <p className="text-xs text-purple-700">
                    Unlimited access
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      );
    }

    if (subscriptionType === 'monthly') {
      return (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Crown className="h-4 w-4 text-blue-600" />
                <div>
                  <Badge className="mb-1 bg-blue-600">
                    Monthly Plan
                  </Badge>
                  <p className="text-xs text-blue-700">
                    Active subscription
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      );
    }
  }

  // Only show trial widgets if there's actually a trial
  if (!hasActiveTrial && !hasExpiredTrial) {
    return null;
  }

  if (hasActiveTrial && timeRemaining > 0) {
    const isLastHour = timeRemaining < 3600000;

    return (
      <Card className={`${isLastHour ? 'border-orange-200 bg-orange-50' : 'border-green-200 bg-green-50'}`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock className={`h-4 w-4 ${isLastHour ? 'text-orange-600' : 'text-green-600'}`} />
              <div>
                <Badge variant={isLastHour ? 'destructive' : 'default'} className="mb-1">
                  Trial Active
                </Badge>
                <p className={`text-xs font-mono font-bold ${isLastHour ? 'text-orange-700' : 'text-green-700'}`}>
                  {formattedTime}
                </p>
              </div>
            </div>
            <Button
              size="sm"
              onClick={handleUpgradeToMonthly}
              className={isLastHour ? 'bg-orange-600 hover:bg-orange-700' : 'bg-green-600 hover:bg-green-700'}
              disabled={isProcessing}
            >
              {isProcessing ? "Processing..." : "Upgrade"}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (hasExpiredTrial) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <div>
                <Badge variant="destructive" className="mb-1">
                  Trial Expired
                </Badge>
                <p className="text-xs text-red-700">
                  Upgrade to continue
                </p>
              </div>
            </div>
            <Button
              size="sm"
              onClick={handleUpgradeToMonthly}
              className="bg-red-600 hover:bg-red-700"
              disabled={isProcessing}
            >
              {isProcessing ? "Processing..." : "Upgrade Now"}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return null;
};
